import { useCallback, useEffect, useState } from "react";
import { captureError } from "@/lib/sentry";
import { Link, useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TeamLogo from "@/components/TeamLogo";
import {
  BroadcastIcon,
  LockKeyIcon,
  LightningIcon,
  ClipboardTextIcon,
  MicrophoneIcon,
  MicrophoneStageIcon,
  WaveformIcon,
  ClockCounterClockwiseIcon,
} from "@phosphor-icons/react";

const PRESS_BOX_TOOLS = [
  { key: "pregame", label: "Pregame Report", desc: "Game preview · trends · injuries · matchups · storylines", icon: ClipboardTextIcon, route: "tool" },
  { key: "postgame", label: "Postgame Report", desc: "Summary · turning points · top performers · quotes · implications", icon: ClipboardTextIcon, route: "tool" },
  { key: "talking-points", label: "Talking Point Generator", desc: "8 broadcast-ready talking points from live game facts", icon: LightningIcon, route: "tool" },
  { key: "historical-comparison", label: "Historical Comparison", desc: "Opinion-framed parallels to past eras, players & teams", icon: ClockCounterClockwiseIcon, route: "tool" },
  { key: "interview", label: "Interview Assistant", desc: "Player + coach question packs in EN/ES with follow-ups", icon: MicrophoneIcon, route: "interview" },
  { key: "press-conference", label: "AI Press Conference", desc: "Interactive simulated Q&A with a player or coach", icon: MicrophoneStageIcon, route: "press-conference" },
  { key: "commentary", label: "Commentary Assistant", desc: "Opening · halftime · closing — TV or radio style", icon: WaveformIcon, route: "commentary" },
];

const TOOL_TITLES = {
  pregame: "Pregame Report",
  postgame: "Postgame Report",
  "talking-points": "Talking Point Generator",
  "historical-comparison": "Historical Comparison",
};

export default function PressBox() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [tool, setTool] = useState(null); // {key,gameId} for pregame/postgame
  const [output, setOutput] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sportFilter, setSportFilter] = useState("ALL");

  const loadGames = useCallback(async () => {
    try {
      const r = await api.get("/games");
      setGames(r.data || []);
    } catch (err) {
      captureError("press box load failed:", err);
    }
  }, []);

  useEffect(() => {
    loadGames();
  }, [loadGames]);

  const isLocked = user?.plan !== "press_box_pro";

  const runTool = async (toolKey, gameId) => {
    if (isLocked) return;
    setLoading(true);
    setOutput(null);
    try {
      const res = await api.post(`/press-box/${toolKey}/${gameId}`);
      setOutput({ key: toolKey, gameId, text: res.data.text });
    } catch (err) {
      captureError(`press box ${toolKey} failed:`, err);
      setOutput({ key: toolKey, gameId, text: "Generation failed. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  // Sports tabs are built from all available leagues; the slate below is filtered to the active sport.
  const LEAGUE_ORDER = ["MLB", "NBA", "NFL", "UFC", "Soccer"];
  const leaguesPresent = LEAGUE_ORDER.filter((lg) => games.some((g) => g.league === lg));
  const sportTabs = ["ALL", ...leaguesPresent];
  const filtered = sportFilter === "ALL" ? games : games.filter((g) => g.league === sportFilter);

  const live = filtered.filter((g) => g.status === "LIVE");
  const upcoming = filtered.filter((g) => g.status === "UPCOMING");
  const final = filtered.filter((g) => g.status === "FINAL");

  const nonLive = [...upcoming, ...final];

  // Which games each tool can run on (pregame → upcoming; postgame → final; others → any).
  const toolGames = (k) => {
    if (k === "pregame") return [...upcoming, ...live];
    if (k === "postgame") return [...final, ...live];
    return [...live, ...upcoming, ...final];
  };

  const byLeague = LEAGUE_ORDER
    .map((lg) => ({ league: lg, items: nonLive.filter((g) => g.league === lg) }))
    .filter((b) => b.items.length > 0);

  return (
    <AppLayout>
      <div className="mb-6 fade-up flex items-end justify-between gap-3 flex-wrap" data-testid="press-box-heading">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF453A] mb-2 inline-flex items-center gap-1.5">
            <span className="live-dot" /> Press Box Mode
          </p>
          <h1 className="font-display font-bold text-4xl md:text-5xl uppercase tracking-tighter">Broadcast Control Room</h1>
          <p className="text-white/60 text-sm mt-2 max-w-2xl">
            Production-grade talking points, pregame + postgame reports, interview packs, and on-air scripts —
            generated live in English, Spanish, or Bilingual.
          </p>
        </div>
        {isLocked && (
          <Link
            to="/pricing?plan=press_box_pro"
            data-testid="press-box-upgrade"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#FDB927] hover:bg-yellow-400 text-black text-xs font-black uppercase tracking-wider"
          >
            <LockKeyIcon weight="fill" /> Unlock Press Box Pro · $299/mo
          </Link>
        )}
      </div>

      {/* Sports tabs — switch the broadcast slate by sport */}
      {sportTabs.length > 1 && (
        <div className="mb-7 flex flex-wrap gap-2 border-b border-white/10 pb-4" data-testid="press-box-sport-tabs">
          {sportTabs.map((s) => {
            const count = s === "ALL" ? games.length : games.filter((g) => g.league === s).length;
            const hasLive = s === "ALL"
              ? games.some((g) => g.status === "LIVE")
              : games.some((g) => g.league === s && g.status === "LIVE");
            const active = sportFilter === s;
            return (
              <button
                key={s}
                onClick={() => setSportFilter(s)}
                data-testid={`sport-tab-${s}`}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider border transition-colors ${
                  active
                    ? "bg-[#FDB927] text-black border-[#FDB927]"
                    : "bg-[#121212] text-white/60 border-white/15 hover:text-white hover:border-white/30"
                }`}
              >
                {hasLive && (
                  <span
                    data-testid={`sport-tab-live-${s}`}
                    className={`w-2 h-2 rounded-full ${active ? "bg-[#FF453A]" : "live-dot"}`}
                    title="Live game in progress"
                  />
                )}
                {s === "ALL" ? "All Sports" : s}
                <span className={`font-num text-[11px] ${active ? "text-black/70" : "text-white/35"}`}>{count}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Live games row — primary CTA: enter Press Box Live */}
      <section className="mb-8">
        <SectionHeading title="Live Now" badge={live.length} testId="section-live" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {live.map((g) => (
            <LiveGameTile key={g.id} game={g} locked={isLocked} onEnter={() => navigate(`/press-box/${g.id}`)} />
          ))}
          {live.length === 0 && (
            <div className="md:col-span-3 p-6 rounded-md bg-[#121212] border border-white/10 text-white/50 text-sm">
              No live games right now — check today's upcoming slate below.
            </div>
          )}
        </div>
      </section>

      {/* Tools strip */}
      <section className="mb-8">
        <SectionHeading title="AI Production Tools" testId="section-tools" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {PRESS_BOX_TOOLS.map((t) => (
            <button
              key={t.key}
              disabled={isLocked}
              data-testid={`tool-${t.key}`}
              onClick={() => {
                if (t.route === "interview") navigate("/press-box/interview");
                else if (t.route === "commentary") navigate("/press-box/commentary");
                else if (t.route === "press-conference") navigate("/press-box/press-conference");
                else setTool({ key: t.key });
              }}
              className={`text-left p-5 rounded-md border transition-colors ${
                isLocked
                  ? "border-white/10 bg-[#121212] opacity-50 cursor-not-allowed"
                  : "border-white/10 bg-[#121212] hover:border-[#FDB927]/50 hover:bg-[#FDB927]/5"
              }`}
            >
              <t.icon weight="bold" size={22} className="text-[#FDB927] mb-3" />
              <h3 className="font-display font-bold text-base uppercase tracking-tight mb-1">{t.label}</h3>
              <p className="text-xs text-white/60">{t.desc}</p>
            </button>
          ))}
        </div>
      </section>

      {/* Pregame / Postgame: pick a game */}
      {tool && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setTool(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl rounded-md bg-[#121212] border border-white/10 p-6 max-h-[90vh] overflow-y-auto" data-testid="tool-picker">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-2xl uppercase tracking-tighter">
                {TOOL_TITLES[tool.key] || "Report"}
              </h2>
              <button onClick={() => { setTool(null); setOutput(null); }} className="text-white/60 hover:text-white text-xs uppercase font-bold tracking-wider">Close</button>
            </div>
            <p className="text-sm text-white/60 mb-4">Pick a game from today's slate:</p>
            <div className="grid grid-cols-1 gap-2 mb-4">
              {toolGames(tool.key).map((g) => (
                <button
                  key={g.id}
                  data-testid={`pick-${tool.key}-${g.id}`}
                  onClick={() => runTool(tool.key, g.id)}
                  disabled={loading}
                  className="text-left px-3 py-2 rounded-md bg-black/40 border border-white/10 hover:border-[#FDB927]/50 text-sm flex items-center justify-between"
                >
                  <span><span className="text-white/40 text-[10px] font-bold uppercase tracking-wider mr-2">{g.league}</span>{g.away_team_abbr} @ {g.home_team_abbr}</span>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-white/40">{g.status === "LIVE" ? g.period_label : g.period_label}</span>
                </button>
              ))}
            </div>
            {loading && <p className="text-sm text-white/60 font-display tracking-wider">Gamly producing your report…</p>}
            {output && (
              <div className="p-4 rounded-md bg-black/50 border border-[#FDB927]/30 max-h-[50vh] overflow-y-auto" data-testid="tool-output">
                <pre className="whitespace-pre-wrap font-sans text-sm text-white/90 leading-relaxed">{output.text}</pre>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Slate grouped by league — MLB first (real-life sports calendar mirror) */}
      <section className="space-y-6">
        {byLeague.map((bucket) => (
          <div key={bucket.league} data-testid={`league-section-${bucket.league}`}>
            <SectionHeading title={bucket.league} badge={bucket.items.length} testId={`section-${bucket.league}`} />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {bucket.items.map((g) => (
                <LeagueGameRow key={g.id} game={g} onEnter={() => navigate(`/press-box/${g.id}`)} locked={isLocked} />
              ))}
            </div>
          </div>
        ))}
        {byLeague.length === 0 && (
          <div className="p-6 rounded-md bg-[#121212] border border-white/10 text-white/50 text-sm">
            No upcoming or final games yet today.
          </div>
        )}
      </section>
    </AppLayout>
  );
}

function SectionHeading({ title, badge, testId }) {
  return (
    <div className="flex items-center gap-2 mb-3" data-testid={testId}>
      <h2 className="font-display font-bold text-2xl md:text-3xl uppercase tracking-tighter">{title}</h2>
      {badge !== undefined && (
        <span className="font-num text-base font-black text-[#FDB927]">{badge}</span>
      )}
    </div>
  );
}

function LiveGameTile({ game, locked, onEnter }) {
  return (
    <div
      className="rounded-md bg-gradient-to-br from-[#1a1a1a] to-[#0d0d0d] border border-[#FF453A]/30 overflow-hidden"
      data-testid={`pb-live-${game.id}`}
    >
      <div className="px-4 py-2 border-b border-white/5 flex items-center justify-between">
        <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white/50 inline-flex items-center gap-1.5">
          {game.league}
          {game.is_real && (
            <span className="px-1.5 py-0.5 rounded bg-[#32D74B]/15 text-[#32D74B] border border-[#32D74B]/30 text-[9px] tracking-[0.15em]">
              LIVE DATA
            </span>
          )}
        </span>
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-[#FF0000]/15 border border-[#FF453A]/40">
          <span className="live-dot" />
          <span className="text-[10px] font-black uppercase tracking-[0.15em] text-[#FF453A]">{game.period_label}</span>
        </span>
      </div>
      <div className="px-4 py-3 space-y-1">
        <Row team={game.away_team_abbr} name={game.away_team} score={game.away_score} color={game.away_team_color} logo={game.away_team_logo} />
        <Row team={game.home_team_abbr} name={game.home_team} score={game.home_score} color={game.home_team_color} logo={game.home_team_logo} />
      </div>
      <div className="px-4 pb-4">
        <button
          onClick={onEnter}
          disabled={locked}
          data-testid={`enter-press-box-${game.id}`}
          className={`w-full py-3 rounded-md font-black text-sm uppercase tracking-wide inline-flex items-center justify-center gap-2 ${
            locked
              ? "bg-white/5 text-white/30 cursor-not-allowed"
              : "bg-[#FDB927] hover:bg-yellow-400 text-black"
          }`}
        >
          {locked ? <><LockKeyIcon weight="fill" /> Press Box Pro</> : <><BroadcastIcon weight="fill" /> Enter Press Box</>}
        </button>
      </div>
    </div>
  );
}

function Row({ team, name, score, color, logo }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2 min-w-0">
        <TeamLogo abbr={team} logo={logo} color={color} size={28} />
        <span className="font-display font-bold uppercase text-xs tracking-tight text-white/85 truncate">{name}</span>
      </div>
      <span className="font-num text-2xl font-black">{score}</span>
    </div>
  );
}

function SecondaryRow({ game, accent }) {
  return (
    <div className="p-3 rounded-md bg-[#121212] border border-white/10 flex items-center justify-between" data-testid={`pb-secondary-${game.id}`}>
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40 mb-0.5">{game.league} · {game.period_label}</p>
        <p className="font-display font-bold text-sm uppercase tracking-tight truncate">{game.away_team_abbr} @ {game.home_team_abbr}</p>
      </div>
      <div className="font-num text-xl font-black" style={{ color: accent }}>{game.away_score}-{game.home_score}</div>
    </div>
  );
}

function pressBoxBtnClass(locked, isFinal) {
  if (locked) return "bg-white/5 text-white/30 cursor-not-allowed";
  if (isFinal) return "bg-[#32D74B]/10 text-[#32D74B] hover:bg-[#32D74B]/20";
  return "bg-[#0A84FF]/10 text-[#0A84FF] hover:bg-[#0A84FF]/20";
}

function pressBoxBtnLabel(locked, isFinal) {
  if (locked) return "Press Box Pro";
  if (isFinal) return "Postgame Press Box →";
  return "Open Press Box →";
}

function LeagueGameRow({ game, onEnter, locked }) {
  const isFinal = game.status === "FINAL";
  const accent = isFinal ? "#32D74B" : "#0A84FF";
  return (
    <div
      className="rounded-md bg-[#121212] border border-white/10 overflow-hidden flex flex-col"
      data-testid={`pb-league-${game.id}`}
    >
      <div className="px-3 py-2 border-b border-white/5 flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.18em]">
        <span className="text-white/40 flex items-center gap-1.5">
          <span>{game.league} · {game.tv || "—"}</span>
          {game.is_real && (
            <span className="px-1.5 py-0.5 rounded bg-[#32D74B]/15 text-[#32D74B] border border-[#32D74B]/30 text-[9px] tracking-[0.15em]" data-testid={`live-data-${game.id}`}>
              LIVE DATA
            </span>
          )}
        </span>
        <span style={{ color: accent }}>{game.period_label}</span>
      </div>
      <div className="px-3 py-3 flex-1 space-y-1">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <TeamLogo abbr={game.away_team_abbr} logo={game.away_team_logo} color={game.away_team_color} size={28} />
            <span className="font-display font-bold uppercase text-xs tracking-tight text-white/80 truncate">{game.away_team}</span>
          </div>
          <span className="font-num text-xl font-black">{game.away_score}</span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <TeamLogo abbr={game.home_team_abbr} logo={game.home_team_logo} color={game.home_team_color} size={28} />
            <span className="font-display font-bold uppercase text-xs tracking-tight text-white/80 truncate">{game.home_team}</span>
          </div>
          <span className="font-num text-xl font-black">{game.home_score}</span>
        </div>
        <p className="text-[10px] text-white/45 truncate pt-1">{game.key_matchup}</p>
      </div>
      <button
        onClick={onEnter}
        disabled={locked}
        data-testid={`pb-enter-${game.id}`}
        className={`px-3 py-2 text-[10px] font-bold uppercase tracking-[0.15em] inline-flex items-center justify-center gap-1 transition-colors ${pressBoxBtnClass(locked, isFinal)}`}
      >
        {pressBoxBtnLabel(locked, isFinal)}
      </button>
    </div>
  );
}

function Empty({ text }) {
  return <div className="p-4 rounded-md bg-[#121212] border border-white/10 text-white/50 text-sm">{text}</div>;
}
